import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import LoadingDots, { activityLoadingTextCss } from './activityLoadingText'
import { readableTextColor } from './activityBackgrounds'

export const FADE_DURATION_MS = 600
export const ACTIVITY_LOADING_PANE_Z_INDEX = 1001

const paneCss = (background, faded) => css`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${background};
  /* Derived: not every activity's colour is dark, and a white label vanishes on the light ones. */
  color: ${readableTextColor(background)};
  text-align: center;
  padding-inline: 32px;
  opacity: ${faded ? 0 : 1};
  transition: opacity ${FADE_DURATION_MS}ms ease-out;
`

/**
 * Full-screen hold shown inside the Together app for activities configured to show it.
 *
 * Fades to reveal content when it is ready.
 */
export default function ActivityLoadingPane({ background, label, fadeOut = false, onFadedOut }) {
  const [painted, setPainted] = useState(false)
  useEffect(() => {
    const frame = requestAnimationFrame(() => setPainted(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const faded = painted && fadeOut

  const onFadedOutRef = useRef(onFadedOut)
  onFadedOutRef.current = onFadedOut
  const settledRef = useRef(false)

  const settle = useCallback(() => {
    if (settledRef.current) return
    settledRef.current = true
    onFadedOutRef.current?.()
  }, [])

  useEffect(() => {
    if (!faded) return undefined

    const timer = setTimeout(settle, FADE_DURATION_MS + 100)
    return () => clearTimeout(timer)
  }, [faded, settle])

  // No dedicated close control here (the retired ActivityCloseButton used to
  // re-render above this hold): the shared ExperienceNavControls controls are
  // position: fixed at a z-index far above this pane, so the kids-return control
  // stays visible and clickable for the whole load.
  return (
    <div
      css={paneCss(background, faded)}
      onTransitionEnd={faded ? settle : undefined}
      data-testid="activity-loading-pane"
    >
      <output aria-live="polite" css={activityLoadingTextCss}>
        {label}
        <LoadingDots />
      </output>
    </div>
  )
}

ActivityLoadingPane.propTypes = {
  /** Colour the activity paints while loading. */
  background: PropTypes.string.isRequired,
  /** Already-localized loading text. */
  label: PropTypes.string,
  /** Fade the pane out to reveal the content painted beneath it. */
  fadeOut: PropTypes.bool,
  /** Called once the fade finishes, so the caller can unmount. */
  onFadedOut: PropTypes.func,
}
